import { useQuery } from '@tanstack/react-query';
import { FAST_POLLING_INTERVAL, QueryKeys } from '../../ui-config/queries';
import { useProtocolDataContext } from '../useProtocolDataContext';
import { IMarketDataResponse } from './useMarketsQuery';
import { FeeAmount } from '@uniswap/v3-sdk';

export interface IPoolDataRequest {
  endpointURL: string;
  token0?: string;
  token1?: string;
  fee?: number;
  marketId?: number;
}

export interface IPoolDataResponse {
  chain_id: number;
  token_0: string;
  token_1: string;
  fee: number;
  address: string;
  tvl_usd: string;
  estimated_usd_fees_per_day: string;
  liquidity: number;
  market: IMarketDataResponse;
}

export const getPoolData = async ({
  endpointURL,
  marketId,
  token0,
  token1,
  fee,
}: IPoolDataRequest) => {
  try {
    const url = `${endpointURL}/concentrated_liquidity/pool?token_0=${token0}&token_1=${token1}&fee=${fee}&market_id=${marketId}`;
    const result = await fetch(url);
    const json: IPoolDataResponse = await result.json();
    return json;
  } catch (e) {
    return null;
  }
};

const INITIAL_DATA = {} as IPoolDataResponse ;

export const usePoolDataQuery = ({
  marketId,
  token0,
  token1,
  feeAmount = 0,
  enabled = true,
}: Partial<Omit<IPoolDataRequest, 'endpointURL' | 'fee'>> & { enabled?: boolean; feeAmount?: FeeAmount }) => {
  const { currentNetworkConfig } = useProtocolDataContext();
  const endpointURL = currentNetworkConfig.yldrYpiUrl;

  const { data, ...rest } = useQuery({
    queryKey: [QueryKeys.LP_POOL, marketId, token0, token1, feeAmount],
    queryFn: () => getPoolData({
      endpointURL,
      marketId,
      token0,
      token1,
      fee: feeAmount,
    }),
    enabled: enabled && Boolean(marketId && token0 && token1),
    refetchInterval: FAST_POLLING_INTERVAL,
  });

  return {
    data: data || INITIAL_DATA,
    ...rest,
  }
}
